import { ShiftModel } from '@/libs/Api';
import {
    startOfProductionDayMs,
    endOfProductionDayMs,
    productionDayKey,
    startOfCalendarDayMs,
} from './production-day.utils';

/**
 * Spočítá plánované minuty produkce pro směny, které se překrývají s rozsahem.
 * 
 * Používá průnik intervalu směny a zadaného intervalu – chrání proti:
 * - Přetaženým směnám (které přesahují zadaný rozsah)
 * - Otevřeným směnám (bez toDate)
 * - Částečným překryvům
 * 
 * Logika:
 * 1. Pro každou směnu spočítáme průnik [směna.from, směna.to] ∩ [from, to]
 * 2. Sečteme délky všech průniků v minutách
 * 
 * Příklad: směna 7.10. 06:00-14:00, rozsah 7.10. 10:00-20:00
 *   → Průnik: 7.10. 10:00-14:00 = 4 hodiny = 240 minut
 * 
 * @param shifts Pole směn
 * @param fromIso Začátek rozsahu (ISO string)
 * @param toIso Konec rozsahu (ISO string)
 * @returns Počet plánovaných minut v rozsahu
 */
export function calcPlannedMinutes(shifts: ShiftModel[] | undefined | null, fromIso: string, toIso: string): number {
    if (!Array.isArray(shifts) || shifts.length === 0) return 0;
    const fromMs = Date.parse(fromIso);
    const toMs = Date.parse(toIso);
    if (!isFinite(fromMs) || !isFinite(toMs)) return 0;
    
    let minutes = 0;
    for (const s of shifts) {
        if (!s?.fromDate) continue;
        
        const sFromMs0 = Date.parse(s.fromDate as string);
        // Pokud směna nemá toDate, použijeme aktuální čas
        const sToMs0 = Date.parse((s.toDate as string) ?? new Date().toISOString());
        
        if (!isFinite(sFromMs0) || !isFinite(sToMs0)) continue;
        
        // Spočítáme průnik: max(začátek směny, začátek rozsahu) až min(konec směny, konec rozsahu)
        const startMs = Math.max(fromMs, sFromMs0);
        const endMs = Math.min(toMs, sToMs0);
        
        // Pokud je průnik kladný, přičteme minuty
        if (endMs > startMs) {
            minutes += Math.floor((endMs - startMs) / 60000);
        }
    }
    return minutes;
}

/**
 * Plánované minuty po pracovištích – průnik směn s intervalem, seskupený dle workplaceId.
 * 
 * Stejná logika jako calcPlannedMinutes, ale výsledek je seskupený po pracovištích.
 * Hodí se pro výpočty OEE a availability po jednotlivých strojích/linkách.
 * 
 * @param shifts Pole směn
 * @param fromIso Začátek rozsahu (ISO string)
 * @param toIso Konec rozsahu (ISO string)
 * @returns Mapa workplaceId → počet plánovaných minut
 */
export function calcPlannedMinutesByWorkplace(shifts: ShiftModel[] | undefined | null, fromIso: string, toIso: string): Map<string | null, number> {
    const map = new Map<string | null, number>();
    if (!Array.isArray(shifts) || shifts.length === 0) return map;
    const fromMs = Date.parse(fromIso);
    const toMs = Date.parse(toIso);
    if (!isFinite(fromMs) || !isFinite(toMs)) return map;
    
    for (const s of shifts) {
        if (!s?.fromDate) continue;
        const sFromMs0 = Date.parse(s.fromDate as string);
        const sToMs0 = Date.parse((s.toDate as string) ?? new Date().toISOString());
        if (!isFinite(sFromMs0) || !isFinite(sToMs0)) continue;
        
        // Průnik intervalů
        const startMs = Math.max(fromMs, sFromMs0);
        const endMs = Math.min(toMs, sToMs0);
        const minutes = endMs > startMs ? Math.floor((endMs - startMs) / 60000) : 0;
        
        // Získáme workplaceId (může být přímo v shift nebo ve vztahu workplace)
        const wid: string | null = (s as any).workplaceId ?? s.workplaceId ?? null;
        
        // Přičteme minuty k danému pracovišti
        map.set(wid, (map.get(wid) || 0) + Math.max(0, minutes));
    }
    return map;
}

/**
 * Denní plánované minuty – průnik směny s konkrétním výrobním dnem, klíč YYYY-MM-DD.
 * Používá výrobní den definovaný v TenantModel.dayStart.
 * 
 * KLÍČOVÁ LOGIKA PRO VÝROBNÍ DNY:
 * - Směna je přiřazena k výrobnímu dni podle svého ZAČÁTKU (fromDate)
 * - Ale počítáme pouze část směny, která spadá do tohoto výrobního dne
 * 
 * Příklad: dayStart = "06:00:00", směna 7.10. 06:00 - 8.10. 08:00 (26 hodin)
 *   - Směna patří k výrobnímu dni 7.10. (začíná v 7.10. 06:00)
 *   - Výrobní den 7.10. trvá 7.10. 06:00 - 8.10. 06:00
 *   - Průnik: 7.10. 06:00 - 8.10. 06:00 = 24 hodin (ne celých 26!)
 *   - Zbylé 2 hodiny (8.10. 06:00-08:00) patří už výrobnímu dni 8.10.
 * 
 * Proces:
 * 1. Inicializujeme mapu se všemi výrobními dny v rozsahu (hodnota 0)
 * 2. Pro každou směnu určíme, do kterého výrobního dne patří (podle začátku)
 * 3. Spočítáme průnik směny s tímto výrobním dnem
 * 4. Přičteme minuty k danému dni
 * 
 * @param shifts Pole směn
 * @param fromIso Začátek rozsahu (ISO string)
 * @param toIso Konec rozsahu (ISO string)
 * @returns Mapa dayKey (YYYY-MM-DD) → počet plánovaných minut
 */
export function calcPlannedMinutesDaily(shifts: ShiftModel[] | undefined | null, fromIso: string, toIso: string): Map<string, number> {
    const map = new Map<string, number>();
    if (!Array.isArray(shifts) || shifts.length === 0) return map;
    const fromMs0 = Date.parse(fromIso);
    const toMs0 = Date.parse(toIso);
    if (!isFinite(fromMs0) || !isFinite(toMs0)) return map;
    
    // Inicializace: Vytvoříme klíče pro všechny výrobní dny v rozsahu
    const fromStart = startOfProductionDayMs(fromMs0);
    const toStart = startOfProductionDayMs(toMs0);
    for (let cur = fromStart; cur <= toStart; ) {
        map.set(productionDayKey(cur), 0);
        const d = new Date(cur);
        // Posuneme se o jeden kalendářní den
        cur = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime();
    }
    
    // Výpočet: Pro každou směnu přiřadíme minuty k odpovídajícímu výrobnímu dni
    for (const s of shifts) {
        if (!s?.fromDate) continue;
        const sFromMs = Date.parse(s.fromDate as string);
        const sToMs = Date.parse((s.toDate as string) ?? (s.fromDate as string));
        if (!isFinite(sFromMs) || !isFinite(sToMs)) continue;
        
        // Klíč výrobního dne podle začátku směny
        const key = productionDayKey(sFromMs);
        
        // Hranice výrobního dne, do kterého směna patří
        const dayStart = startOfProductionDayMs(sFromMs);
        const nextDayStart = endOfProductionDayMs(sFromMs);
        
        // Průnik směny s tímto výrobním dnem
        const startMs = Math.max(sFromMs, dayStart);
        const endMs = Math.min(sToMs, nextDayStart);
        const minutes = endMs > startMs ? Math.floor((endMs - startMs) / 60000) : 0;
        
        // Přičteme minuty
        map.set(key, (map.get(key) || 0) + Math.max(0, minutes));
    }
    return map;
}

/**
 * Denní plánované minuty po pracovištích. Klíč: YYYY-MM-DD -> (workplaceId -> minutes)
 * Pozn.: směny zařazujeme do výrobního dne podle začátku směny,
 * a počítáme pouze průnik se zvoleným výrobním dnem.
 */
export function calcPlannedMinutesDailyByWorkplace(
    shifts: ShiftModel[] | undefined | null,
    fromIso: string,
    toIso: string
): Map<string, Map<string | null, number>> {
    const map = new Map<string, Map<string | null, number>>();
    if (!Array.isArray(shifts) || shifts.length === 0) return map;
    const fromMs0 = Date.parse(fromIso);
    const toMs0 = Date.parse(toIso);
    if (!isFinite(fromMs0) || !isFinite(toMs0)) return map;
    const fromStart = startOfProductionDayMs(fromMs0);
    const toStart = startOfProductionDayMs(toMs0);
    for (let cur = fromStart; cur <= toStart; ) {
        map.set(productionDayKey(cur), new Map<string | null, number>());
        const d = new Date(cur);
        cur = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime();
    }
    for (const s of shifts) {
        if (!s?.fromDate) continue;
        const sFromMs = Date.parse(s.fromDate as string);
        const sToMs = Date.parse((s.toDate as string) ?? (s.fromDate as string));
        if (!isFinite(sFromMs) || !isFinite(sToMs)) continue;
        const key = productionDayKey(sFromMs);
        if (!map.has(key)) map.set(key, new Map<string | null, number>());
        const dayStart = startOfProductionDayMs(sFromMs);
        const nextDayStart = endOfProductionDayMs(sFromMs);
        const startMs = Math.max(sFromMs, dayStart);
        const endMs = Math.min(sToMs, nextDayStart);
        const minutes = endMs > startMs ? Math.floor((endMs - startMs) / 60000) : 0;
        const wid: string | null = (s as any).workplaceId ?? s.workplaceId ?? null;
        const inner = map.get(key)!;
        inner.set(wid, (inner.get(wid) || 0) + Math.max(0, minutes));
    }
    return map;
}

/**
 * Ořízne datum do intervalu [from, to]. Využívá se pro „clamping“ při heatmapách.
 */
type MomentLike = { isBefore: (x: any) => boolean; isAfter: (x: any) => boolean; clone: () => any };
export function clampToRange(d: MomentLike, from: MomentLike, to: MomentLike): any {
    if (d.isBefore(from)) return from.clone();
    if (d.isAfter(to)) return to.clone();
    return d;
}

function pad2(n: number): string { return n < 10 ? `0${n}` : `${n}`; }
function dayKeyFromMs(ms: number): string {
    const d = new Date(ms);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function startOfDayLocalMs(ms: number): number {
    const d = new Date(ms);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

// Re-export production day utilities for backward compatibility
export { startOfProductionDayMs, endOfProductionDayMs, productionDayKey } from './production-day.utils';


